import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../config/api-client';
import { queryKeys } from '../config/query-keys';
import { useProfileQuery } from './use-profile-query';
import type { ExpatGuideTopic } from '../types/expat-guides';

// Mirrors mobile/src/hooks/use-expat-guides-query.ts.
//
// Response shape (with the API's TransformInterceptor + DTO double wrap):
//   GET /expat-guides/for-you  → { data: { data: ExpatGuideTopic[] } }
//   GET /expat-guides?country= → { data: { data: ExpatGuideTopic[] } }
//   GET /expat-guides/search?q=→ { data: { data: ExpatGuideTopic[] } }
//   GET /expat-guides/:id      → { data: ExpatGuideTopic }
//
// The api-client unwraps the outer `{data, timestamp}` once, so the hook
// must unwrap the inner DTO too.

export function useGuidesQuery(options?: { readonly enabled?: boolean }) {
  const profile = useProfileQuery({ enabled: options?.enabled ?? true });
  const lang = profile.data?.primaryLanguage ?? 'en';
  return useQuery({
    queryKey: queryKeys.guides.forYou(lang),
    queryFn: async () => {
      const result = await apiClient.get<{ data: { data: ExpatGuideTopic[] } }>(
        '/expat-guides/for-you',
      );
      return result.data.data.data;
    },
    staleTime: 30 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}

export function useGuidesByCountryQuery(countryCode: string | null) {
  const profile = useProfileQuery();
  const lang = profile.data?.primaryLanguage ?? 'en';
  return useQuery({
    queryKey: queryKeys.guides.byCountry(countryCode ?? '', lang),
    queryFn: async () => {
      const result = await apiClient.get<{ data: { data: ExpatGuideTopic[] } }>(
        `/expat-guides?countryCode=${encodeURIComponent(countryCode!)}`,
      );
      return result.data.data.data;
    },
    enabled: !!countryCode,
    staleTime: 30 * 60 * 1000,
  });
}

export function useGuideSearchQuery(query: string) {
  const profile = useProfileQuery();
  const lang = profile.data?.primaryLanguage ?? 'en';
  const trimmed = query.trim();
  return useQuery({
    queryKey: queryKeys.guides.search(trimmed, lang),
    queryFn: async () => {
      const result = await apiClient.get<{ data: { data: ExpatGuideTopic[] } }>(
        `/expat-guides/search?q=${encodeURIComponent(trimmed)}`,
      );
      return result.data.data.data;
    },
    enabled: trimmed.length >= 2,
    staleTime: 30 * 60 * 1000,
  });
}

export function useExpatGuideDetailQuery(id: number | null) {
  const profile = useProfileQuery();
  const lang = profile.data?.primaryLanguage ?? 'en';
  return useQuery({
    queryKey: queryKeys.guides.detail(id ?? -1, lang),
    queryFn: async () => {
      const result = await apiClient.get<{ data: ExpatGuideTopic }>(
        `/expat-guides/${id}`,
      );
      return result.data.data;
    },
    enabled: id != null && id >= 0,
    staleTime: 30 * 60 * 1000,
  });
}
